import axios, { type AxiosAdapter, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { ref } from 'vue'

/**
 * How long a fleet read is reused before asking again. The Pinia Colada reads (My work, releases, QA queue,
 * team, quality, metrics) keep their own staleTime; these are the fleet stores' reads, which every page that
 * shows the fleet's state shares. Each is shorter than the slowest poll that reads it, so a poll still gets
 * a fresh answer. Anything else is only shared by reads asked within two seconds of each other, under the
 * 5-second live poll of a run drawer.
 */
export const FLEET_READ_TTL_MS: [RegExp, number][] = [
  [/^\/(health|loops|upcoming)\b/, 60_000],
  [/^\/(runners|insights)\b/, 25_000]
]
export const FLEET_READ_DEFAULT_TTL_MS = 2_000

/** A read that failed with no answer while the browser says it is online is a network change. */
export const NETWORK_CHANGE_RETRY_MS = 1_500
/** While the browser is offline, reads wait for it to come back, checking at least this often. */
export const OFFLINE_RECHECK_MS = [2_000, 5_000, 10_000, 30_000]
/** After this long offline a read gives up, and the page shows its own error. */
export const OFFLINE_GIVE_UP_MS = 5 * 60_000

/** True while a fleet read is waiting for the browser to come back online. */
export const fleetOffline = ref(false)

interface Held {
  at: number
  response: AxiosResponse
}

const held = new Map<string, Held>()
const inFlight = new Map<string, Promise<AxiosResponse>>()
/** Moves on every drop, so a read that started before it is neither kept nor joined by a later one. */
let generation = 0

/** Drops every held read: a write changed something, or the person asked for fresh data. */
export function invalidateFleetReads() {
  generation++
  held.clear()
  inFlight.clear()
}

export function fleetReadTtl(path: string): number {
  return FLEET_READ_TTL_MS.find(([pattern]) => pattern.test(path))?.[1] ?? FLEET_READ_DEFAULT_TTL_MS
}

const isNetworkFailure = (error: unknown): boolean =>
  axios.isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED' && error.code !== 'ECONNABORTED'

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/** Resolves when the browser is back online, or false once it has been offline too long. */
async function onlineAgain(now: () => number): Promise<boolean> {
  const started = now()
  let attempt = 0
  while (isOffline()) {
    if (now() - started >= OFFLINE_GIVE_UP_MS) return false
    const wait = OFFLINE_RECHECK_MS[Math.min(attempt++, OFFLINE_RECHECK_MS.length - 1)]!
    await Promise.race([sleep(wait), new Promise(resolve => window.addEventListener('online', resolve, { once: true }))])
  }
  return true
}

/**
 * A request that failed with no answer. Offline, it waits for the network and tries again; online, it
 * was a network change (VPN reconnecting, Wi-Fi roaming, waking from sleep), so it tries once more after a
 * moment. Either way the page sees an error only when the retry fails too.
 */
async function sendRetryingNetworkChanges(send: () => Promise<AxiosResponse>, now: () => number): Promise<AxiosResponse> {
  try {
    return await send()
  } catch (error) {
    if (!isNetworkFailure(error)) throw error
    if (isOffline()) {
      fleetOffline.value = true
      const back = await onlineAgain(now).finally(() => {
        fleetOffline.value = false
      })
      if (!back) throw error
    } else {
      await sleep(NETWORK_CHANGE_RETRY_MS)
    }
    return send()
  }
}

const pathOf = (config: InternalAxiosRequestConfig) => {
  const url = config.url ?? ''
  return url.startsWith('/') ? url : `/${url}`
}

/**
 * The fleet client's transport: one request for reads asked at the same time, reads answered from a
 * short shared cache, writes clearing it, and network changes retried quietly. Each caller gets its own
 * copy of the answer, so a store that edits what it was given cannot change another page's.
 */
export function attachFleetTransport(client: AxiosInstance, now: () => number = Date.now) {
  // A stand-in instance (a unit test's stub of axios.create) has no transport to wrap.
  if (!client.defaults) return
  const baseSetting = client.defaults.adapter
  let resolved: AxiosAdapter | null = null
  const base: AxiosAdapter = config => (resolved ??= axios.getAdapter(baseSetting) as AxiosAdapter)(config)
  const copy = (response: AxiosResponse, config: InternalAxiosRequestConfig): AxiosResponse => ({
    ...response,
    config,
    data: structuredClone(response.data)
  })

  client.defaults.adapter = async config => {
    const method = (config.method ?? 'get').toLowerCase()
    const send = () => sendRetryingNetworkChanges(() => base(config), now)
    if (method !== 'get') {
      // A write is sent once, never retried: a second POST would start a second run. The held reads are
      // dropped before it and again once it settles, so a read made while it ran is not kept.
      invalidateFleetReads()
      try {
        return await base(config)
      } finally {
        invalidateFleetReads()
      }
    }
    const key = client.getUri(config)
    const hit = held.get(key)
    if (hit && now() - hit.at < fleetReadTtl(pathOf(config))) return copy(hit.response, config)
    let pending = inFlight.get(key)
    if (!pending) {
      const startedIn = generation
      const read: Promise<AxiosResponse> = send()
        .then(response => {
          if (startedIn === generation) held.set(key, { at: now(), response })
          return response
        })
        .finally(() => {
          if (inFlight.get(key) === read) inFlight.delete(key)
        })
      pending = read
      inFlight.set(key, read)
    }
    return copy(await pending, config)
  }
}
